import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/lib/supabase";

interface PricingVariant {
  id: string;
  label: string;
  price: number;
  child?: number;
  infant?: number;
}

interface ExcursionPricing {
  slug: string;
  variants: PricingVariant[];
}

function applySupabasePrices<T extends { slug: string; variants: { id: string; price: number; child?: number; infant?: number }[]; priceFrom: number }>(
  excursions: T[],
  pricingMap: Map<string, PricingVariant[]>,
): T[] {
  return excursions.map((exc) => {
    const supabaseVariants = pricingMap.get(exc.slug);
    if (!supabaseVariants || supabaseVariants.length === 0) return exc;

    let minPrice = Infinity;
    const mergedVariants = exc.variants.map((v) => {
      const match = supabaseVariants.find((sv) => sv.id === v.id);
      const price = match ? match.price : v.price;
      const child = match?.child ?? v.child;
      const infant = match?.infant ?? v.infant;
      // Ignore free tiers (price 0) when computing the "from" price.
      if (price > 0 && price < minPrice) minPrice = price;
      return { ...v, price, child, infant };
    });

    return { ...exc, variants: mergedVariants, priceFrom: minPrice === Infinity ? exc.priceFrom : minPrice };
  });
}

export function useSupabasePrices<T extends { slug: string; variants: { id: string; price: number; child?: number; infant?: number }[]; priceFrom: number }>(
  excursions: T[],
): { data: T[]; loading: boolean; error: string | null } {
  const [pricingMap, setPricingMap] = useState<Map<string, PricingVariant[]> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchPricing() {
      try {
        const { data, error: rpcError } = await supabase
          .from("excursions_pricing")
          .select("slug, variants");

        if (cancelled) return;

        if (rpcError) throw rpcError;

        const map = new Map<string, PricingVariant[]>();
        (data as ExcursionPricing[]).forEach((row) => {
          map.set(row.slug, row.variants);
        });
        setPricingMap(map);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load prices");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPricing();
    return () => { cancelled = true; };
  }, []);

  const result = useMemo(() => {
    if (!pricingMap) return excursions;
    return applySupabasePrices(excursions, pricingMap);
  }, [excursions, pricingMap]);

  return { data: result, loading, error };
}